import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Segmented,
    Table,
    DatePicker,
    Select,
    Input,
    Button,
    Tooltip,
    message,
    Empty,
    Switch,
} from 'antd';
import { FaSync, FaChevronRight } from 'react-icons/fa';
import moment from 'moment';

import {
    listGymOrders,
    listGymSponsors,
    getGymHealthIssues,
} from '../../services/api/gym-enrolments';
import NavBar from '../layout/NavBar';
import OrderDetailsDrawer from './OrderDetailsDrawer';
import { healthBadge, sourceBadge, groupStatusBadge } from './common/badges';

import './GymEnrolments.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const BRAND = '#714FFF';
const SURFACE = '#FFFFFF';
const BG = '#F2F1F6';
const BORDER = '#E7E5F2';
const TEXT = '#1F2937';
const MUTED = '#6B7280';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Gym/Fitpass ops dashboard.
 *
 * Compact single-bar layout (mirrors /revamp-offline-consults): one
 * horizontal filter row, dense table below, slide-in drawer for detail.
 * Tabs (Fitpass | Cult) keep partner state shareable via ?partner=.
 */
const GymEnrolments = () => {
    const initialPartner =
        new URLSearchParams(window.location.search).get('partner') === 'cult'
            ? 'cult'
            : 'fitpass';
    const [activePartner, setActivePartner] = useState(initialPartner);

    const handleTabChange = useCallback((key) => {
        setActivePartner(key);
        const url = new URL(window.location.href);
        url.searchParams.set('partner', key);
        window.history.replaceState(null, '', url.toString());
    }, []);

    return (
        <div className="gym-ops-shell min-h-screen" style={{ background: BG }}>
            <NavBar />
            <div
                className="flex items-center justify-between flex-wrap gap-3"
                style={{
                    background: BG,
                    padding: '14px 20px 10px',
                    marginTop: 72,
                    position: 'sticky',
                    top: 0,
                    zIndex: 11,
                    borderBottom: `1px solid ${BORDER}`,
                }}
            >
                <div>
                    <h1
                        className="m-0"
                        style={{
                            color: TEXT,
                            fontSize: 20,
                            fontWeight: 600,
                            lineHeight: '24px',
                        }}
                    >
                        Gym Enrolments
                    </h1>
                    <div
                        style={{
                            fontSize: 12,
                            color: MUTED,
                            marginTop: 2,
                        }}
                    >
                        Orders, members, refunds, diagnostics
                    </div>
                </div>
                <Segmented
                    value={activePartner}
                    onChange={handleTabChange}
                    options={[
                        { label: 'Fitpass', value: 'fitpass' },
                        { label: 'Cult', value: 'cult' },
                    ]}
                />
            </div>

            <div style={{ padding: '10px 20px 20px' }}>
                <GymEnrolmentsTab partner={activePartner} />
            </div>
        </div>
    );
};

/** Approximate sticky-header height = title row (~74px). The filter
 *  bar uses this as its `top` so it parks right under the title. */
const HEADER_STICKY_HEIGHT = 74;

/**
 * Partner-scoped table + filters. Filters render as a single
 * horizontal row that wraps when narrow; the table flexes below.
 */
const GymEnrolmentsTab = ({ partner }) => {
    const [dateRange, setDateRange] = useState(null);
    const [status, setStatus] = useState(null);
    const [sourceType, setSourceType] = useState(null);
    const [sponsorId, setSponsorId] = useState(null);
    const [search, setSearch] = useState('');
    const [issuesOnly, setIssuesOnly] = useState(false);

    const [sponsors, setSponsors] = useState([]);
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [issueSummary, setIssueSummary] = useState(null);

    const [drawerGroupKey, setDrawerGroupKey] = useState(null);

    useEffect(() => {
        let cancelled = false;
        listGymSponsors(partner)
            .then((data) => !cancelled && setSponsors(data || []))
            .catch(() => !cancelled && setSponsors([]));
        return () => {
            cancelled = true;
        };
    }, [partner]);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                partner,
                page,
                pageSize: DEFAULT_PAGE_SIZE,
                ...(dateRange && dateRange[0]
                    ? { startDate: dateRange[0].format('YYYY-MM-DD') }
                    : {}),
                ...(dateRange && dateRange[1]
                    ? { endDate: dateRange[1].format('YYYY-MM-DD') }
                    : {}),
                ...(status ? { status } : {}),
                ...(sourceType ? { sourceType } : {}),
                ...(sponsorId ? { sponsorId } : {}),
                ...(search ? { search } : {}),
                ...(issuesOnly ? { issuesOnly: 'true' } : {}),
            };
            const data = await listGymOrders(params);
            setRows(data.rows || []);
            setCount(data.count || 0);
        } catch (err) {
            message.error(err?.message || 'Failed to load gym orders');
            setRows([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    }, [partner, page, dateRange, status, sourceType, sponsorId, search, issuesOnly]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    useEffect(() => {
        if (!issuesOnly) {
            setIssueSummary(null);
            return undefined;
        }
        let cancelled = false;
        const params = {
            partner,
            page: 0,
            pageSize: 200,
            ...(dateRange && dateRange[0]
                ? { startDate: dateRange[0].format('YYYY-MM-DD') }
                : {}),
            ...(dateRange && dateRange[1]
                ? { endDate: dateRange[1].format('YYYY-MM-DD') }
                : {}),
            ...(sponsorId ? { sponsorId } : {}),
            ...(search ? { search } : {}),
        };
        getGymHealthIssues(params)
            .then((data) => !cancelled && setIssueSummary(data))
            .catch(() => !cancelled && setIssueSummary(null));
        return () => {
            cancelled = true;
        };
    }, [issuesOnly, partner, dateRange, sponsorId, search]);

    const onResetFilters = () => {
        setDateRange(null);
        setStatus(null);
        setSourceType(null);
        setSponsorId(null);
        setSearch('');
        setIssuesOnly(false);
        setPage(0);
    };

    const columns = useMemo(
        () =>
            buildColumns({
                onOpenDetails: (groupKey) => setDrawerGroupKey(groupKey),
            }),
        [],
    );

    return (
        <div>
            <div
                className="flex items-center gap-2 flex-wrap"
                style={{
                    background: '#FFFFFF',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    padding: '8px 10px',
                    marginBottom: 10,
                    position: 'sticky',
                    top: HEADER_STICKY_HEIGHT,
                    zIndex: 10,
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
            >
                <Input.Search
                    allowClear
                    placeholder="Search phone / email / order id / partner member id"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onSearch={() => setPage(0)}
                    style={{ flex: '1 1 240px', minWidth: 200, maxWidth: 360 }}
                    size="middle"
                />
                <RangePicker
                    value={dateRange}
                    onChange={(v) => {
                        setDateRange(v);
                        setPage(0);
                    }}
                    size="middle"
                    style={{ width: 240 }}
                    allowClear
                    format="DD MMM"
                />
                <Select
                    allowClear
                    placeholder="Status"
                    value={status}
                    onChange={(v) => {
                        setStatus(v);
                        setPage(0);
                    }}
                    size="middle"
                    style={{ width: 130 }}
                >
                    <Option value="pending">Pending</Option>
                    <Option value="active">Active</Option>
                    <Option value="refunded">Refunded</Option>
                    <Option value="failed">Partner failed</Option>
                </Select>
                <Select
                    allowClear
                    placeholder="Source"
                    value={sourceType}
                    onChange={(v) => {
                        setSourceType(v);
                        setPage(0);
                    }}
                    size="middle"
                    style={{ width: 120 }}
                >
                    <Option value="purchase">Purchase</Option>
                    <Option value="direct">Direct</Option>
                </Select>
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    placeholder="Sponsor"
                    value={sponsorId}
                    onChange={(v) => {
                        setSponsorId(v);
                        setPage(0);
                    }}
                    size="middle"
                    style={{ width: 160 }}
                >
                    {sponsors.map((s) => (
                        <Option key={s.sponsorId} value={s.sponsorId}>
                            {s.sponsorName}
                        </Option>
                    ))}
                </Select>

                <div
                    className="flex items-center gap-1"
                    style={{ marginLeft: 'auto' }}
                >
                    <span
                        className="text-xs text-gray-500"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        Issues only
                    </span>
                    <Switch
                        size="small"
                        checked={issuesOnly}
                        onChange={(v) => {
                            setIssuesOnly(v);
                            setPage(0);
                        }}
                    />
                    <Button size="middle" onClick={onResetFilters}>
                        Reset
                    </Button>
                    <Tooltip title="Refresh">
                        <Button
                            size="middle"
                            icon={<FaSync />}
                            onClick={fetchRows}
                        />
                    </Tooltip>
                </div>
            </div>

            {issuesOnly && issueSummary && (
                <div
                    className="flex items-center gap-3 flex-wrap"
                    style={{
                        background: '#FEF6E0',
                        border: '1px solid #FBE3A0',
                        borderRadius: 6,
                        padding: '6px 10px',
                        marginBottom: 8,
                        fontSize: 12,
                    }}
                >
                    <span className="font-semibold" style={{ color: '#92400E' }}>
                        {issueSummary.count} issue
                        {issueSummary.count === 1 ? '' : 's'}
                    </span>
                    {Object.entries(issueSummary.byCode || {})
                        .filter(([, v]) => v > 0)
                        .map(([k, v]) => (
                            <span key={k} style={{ color: '#6B5B23' }}>
                                {k}={v}
                            </span>
                        ))}
                </div>
            )}

            <div
                style={{
                    background: '#FFFFFF',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
            >
                <Table
                    rowKey="groupKey"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    size="middle"
                    pagination={{
                        current: page + 1,
                        pageSize: DEFAULT_PAGE_SIZE,
                        total: count,
                        onChange: (p) => setPage(p - 1),
                        showSizeChanger: false,
                        size: 'small',
                        showTotal: (t) => `${t} total`,
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    partner === 'cult'
                                        ? 'Cult onboarding launches in V3 — no rows yet.'
                                        : 'No rows match these filters.'
                                }
                            />
                        ),
                    }}
                    onRow={(record) => ({
                        onClick: () => setDrawerGroupKey(record.groupKey),
                        style: { cursor: 'pointer' },
                    })}
                />
            </div>

            <OrderDetailsDrawer
                groupKey={drawerGroupKey}
                onClose={() => setDrawerGroupKey(null)}
            />
        </div>
    );
};

const buildColumns = ({ onOpenDetails }) => [
    {
        title: 'Created',
        dataIndex: 'createdAt',
        width: 120,
        render: (v) => (v ? moment(v).format('DD MMM HH:mm') : '—'),
    },
    {
        title: 'Source',
        dataIndex: 'sourceType',
        width: 90,
        render: (v) => sourceBadge(v),
    },
    {
        title: 'Holder',
        key: 'holder',
        render: (_, r) => (
            <div className="leading-tight">
                <div className="text-sm font-medium">
                    {r.holderName || '—'}
                </div>
                <div className="text-xs text-gray-500">
                    {r.holderPhone || r.holderEmail || `user ${r.holderUserId ?? '—'}`}
                </div>
            </div>
        ),
    },
    {
        title: 'Sponsor',
        dataIndex: 'sponsorName',
        responsive: ['md'],
        ellipsis: true,
        render: (v) => v || '—',
    },
    {
        title: 'Plan',
        dataIndex: 'planTitle',
        responsive: ['lg'],
        ellipsis: true,
        render: (v) => v || '—',
    },
    {
        title: '#',
        dataIndex: 'memberCount',
        width: 50,
        align: 'center',
    },
    {
        title: 'Total',
        dataIndex: 'totalAmount',
        width: 90,
        responsive: ['sm'],
        render: (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`,
    },
    {
        title: 'Status',
        dataIndex: 'groupStatus',
        width: 120,
        render: (v) => groupStatusBadge(v),
    },
    {
        title: 'Health',
        key: 'health',
        width: 160,
        render: (_, r) => (
            <Tooltip
                title={
                    r.failureCodes && r.failureCodes.length > 0
                        ? `Codes: ${r.failureCodes.join(', ')}`
                        : 'All checks passing'
                }
            >
                <span>{healthBadge(r.healthState)}</span>
            </Tooltip>
        ),
    },
    {
        title: '',
        key: 'actions',
        width: 40,
        align: 'right',
        render: (_, r) => (
            <Button
                size="small"
                type="text"
                icon={<FaChevronRight />}
                style={{ color: MUTED }}
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenDetails(r.groupKey);
                }}
            />
        ),
    },
];

export default GymEnrolments;
